<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col
        v-if="isEdit && isCancelled"
        col sm="12">
        <d-alert
          show
          theme="danger">
          To zamówienie jest anulowane i nie można już go edytować
        </d-alert>
      </d-col>
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Zamówienia</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja zamówienia</template>
          <template v-else>Dodawanie zamówienia</template>
        </h3>
        <small class="mt-2">
          DEADLINE:
          <span
            v-if="!data.planned_shipment_date"
            class="text-danger">
            nie określono
          </span>

          <span
            v-if="data.planned_shipment_date"
            class="text-primary">
            {{ data.planned_shipment_date | dateFormat }}
          </span>
        </small>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end order-action-buttons">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/zamowienia')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>

        <span id="order-save-changes-button">
          <d-button
            v-if="!isCancelled"
            theme="success"
            @click.prevent="validateForm"
            :disabled="$refs['order-creator'] && $refs['order-creator'].invalidDatesInCreator">
            Zapisz zmiany
          </d-button>
        </span>

        <d-tooltip
          v-if="$refs['order-creator'] && $refs['order-creator'].invalidDatesInCreator && data.planned_shipment_date"
          target="#order-save-changes-button"
          container=".order-action-buttons">
          Zapis jest niemożliwy, ponieważ co najmniej jedna z dat realizacji ({{ $refs['order-creator'].invalidDatesList.join(', ') }}) w tym zamówieniu wykracza poza ustalony w zamówieniu DEADLINE: {{ data.planned_shipment_date.toLocaleDateString('pl-PL') }} (zakładka "Dane ogólne")
        </d-tooltip>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs
          :nav-class="validationResults.concat(historyErrors)"
          card>
          <!-- Dane ogólne -->
          <d-tab
            title="Dane ogólne"
            :active="!isEdit">
            <d-alert
              v-if="!data.can_edit"
              show
              theme="warning">
              Dane tego zamówienia mogą być edytowane tylko z poziomu HPL
            </d-alert>

            <h4 class="mb-3 pb-3 border-bottom">
              Dane ogólne
            </h4>

            <div
              v-if="!allDataLoaded"
              class="pb-3 pt-3 text-center">
              <div
                class="spinner-border spinner-border-sm"
                role="status"></div>
              Trwa ładowanie danych&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
            </d-alert>

            <d-row v-if="data.created_at">
              <d-col cols="12" class="mb-2">
                <p>
                  Utworzono: <strong class="text-dark">{{ data.created_at }}</strong><br>

                  <template v-if="data.crm_id">
                    <a :href="hplUrl + 'zakupy/edycja/' + parseInt(data.crm_id, 10)" target="_blank" rel="noopener noreferrer">
                      (Zobacz zamówienie w HPL)
                    </a>
                  </template>
                </p>
              </d-col>
            </d-row>

            <validation-observer
              ref="order-form"
              tag="form"
              @keydown.enter.prevent>
              <div class="form-group">
                <label for="order_name">
                  Nazwa zamówienia:
                </label>

                <validation-provider
                  rules="required"
                  name="order_name"
                  v-slot="{ errors }">
                  <d-input
                    :disabled="!data.can_edit || isCancelled"
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.order_name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="customer_name">
                  Nazwa klienta:
                </label>

                <validation-provider
                  rules="required"
                  name="customer_id"
                  v-slot="{ errors }">
                  <select-customer
                    v-if="allDataLoaded && data.can_edit && !isCancelled"
                    :state="errors.length ? 'invalid' : null"
                    :customerID="data.customer_id" />
                  <d-input
                    v-else
                    :disabled="true"
                    v-model="data.customer_name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>

                <div
                  v-if="data.sub_customer_name"
                  class="form-group mt-3">
                  <label>
                    <i class="material-icons text-success">new_releases</i>
                    Nazwa wykonawcy 1:
                  </label>

                  <d-input
                    :disabled="true"
                    v-model="data.sub_customer_name" />
                </div>
              </div>

              <d-row>
                <d-col cols="3">
                  <div class="form-group">
                    <d-checkbox inline toggle-small v-model="data.invoiced" toggle>
                      Zafakturowane?
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col
                  v-if="data.invoiced"
                  cols="9">
                  <div class="form-group">
                    <label for="order_name">
                      Numer faktury:
                    </label>

                    <d-input
                      v-model="data.invoice_number" />
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="planned_shipment_date">
                  DEADLINE:
                </label>

                <validation-provider
                  rules="required"
                  name="planned_shipment_date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="data.planned_shipment_date"
                    :disabled="!data.can_edit || isCancelled"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :class="errors.length ? 'is-invalid' : null"
                    placeholder="Wybierz datę"
                    :language="polishTranslationForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  Uwagi:
                </label>

                <d-textarea
                  :disabled="!data.can_edit || isCancelled"
                  v-model="data.description"
                  :value.prop="data.description"
                  :rows="5"
                  placeholder="Wpisz swoje uwagi..." />
              </div>

              <div
                v-if="data.crm_has_order_file"
                class="form-group">
                <label>
                  Plik dołączony do zamówienia:
                </label>
                <div>
                  <a
                    :href="data.crm_order_file_url"
                    target="_blank"
                    rel="noopener noreferrer">
                    <i class="material-icons">cloud_download</i>
                    Pobierz plik
                  </a>
                </div>
              </div>
            </validation-observer>
          </d-tab>

          <d-tab
            :title="orderTabTitle"
            :active="isEdit">
            <d-alert
              v-if="!data.can_edit"
              show
              theme="warning">
              Dane tego zamówienia mogą być edytowane tylko z poziomu HPL
            </d-alert>

            <order-creator
              ref="order-creator"
              :canEdit="data.can_edit && !isCancelled"
              :products="products"
              :procedures="procedures"
              :colors="colors"
              :materials="materials"
              :polars="polars"
              :seams="seams"
              :strands="strands"
              :workers="workers"
              :drivers="drivers"
              :sizes="sizes"
              :realizationDate="data.planned_shipment_date"
              :productsHasUsages="data.products_has_usages" />
          </d-tab>

          <d-tab title="Praca zdalna">
            <p
              v-if="!isEdit"
              class="text-center">
              Praca zdalna będzie dostepna po zapisaniu zamówienia&hellip;
            </p>

            <remote-job
              ref="remote-job"
              :canEdit="data.can_edit && !isCancelled"
              :orderID="parseInt($route.params.id, 10)"
              :products="products"
              :procedures="procedures"
              :colors="colors"
              :materials="materials"
              :polars="polars"
              :seams="seams"
              :strands="strands"
              :workers="workers"
              :drivers="drivers"
              :sizes="sizes" />
          </d-tab>

          <d-tab title="Historia zmian">
            <p
              v-if="!isEdit"
              class="text-center">
              Historia zmian będzie dostepna po zapisaniu zamówienia&hellip;
            </p>

            <order-evidence
              v-if="isEdit"
              :orderID="parseInt($route.params.id, 10)" />
          </d-tab>

          <d-tab
            :disabled="!data.crm_id"
            title="Historia synchronizacji">
            <p class="text-right">
              <d-button
                v-if="logs && logs.length && data.has_unread_logs"
                :disabled="markingNotificationsAsReadInProgress"
                theme="success"
                class="mb-2"
                @click.prevent="markAllNotificationsAsRead">
                <div
                  v-if="markingNotificationsAsReadInProgress"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <template v-else>
                  Przeczytałem wszystkie
                </template>
              </d-button>
            </p>

            <d-alert
              v-if="(!logs || !logs.length)"
              show
              theme="warning">
              Brak historii synchronizacji
            </d-alert>

            <table
              v-if="logs && logs.length"
              :class="{
                'table mt-0 mb-0 full-width': true,
                'is-loading': loadingLogs
              }">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0 pl-0 pr-0" style="width: 30px;"></th>
                  <th scope="col" class="border-0" style="width: 100px;">Data</th>
                  <th scope="col" class="border-0" style="width: 200px;">Zmiana dotyczy</th>
                  <th scope="col" class="border-0" style="width: 150px;">Rodzaj zmiany</th>
                  <th scope="col" class="border-0" style="width: 150px;">Zmienione pole</th>
                  <th scope="col" class="border-0">Obecna wartość</th>
                  <th scope="col" class="border-0">Poprzednia wartość</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(logItem, index) of logs"
                  :key="'log-item-' + index">
                  <td class="align-middle pl-0 pr-0">
                    <d-badge
                      v-if="logItem.is_read"
                      theme="success"
                      class="px-1"
                      title="Przeczytano">
                      ✔
                    </d-badge>
                    <d-badge
                      v-if="!logItem.is_read"
                      theme="danger"
                      class="px-2"
                      title="Nie przeczytano">
                      !
                    </d-badge>
                  </td>
                  <td class="align-middle">
                    <small>{{ logItem.created_at }}</small>
                  </td>
                  <td class="align-middle">
                    <small v-if="logItem.source === 'ORDER'">Zamówienia</small>
                    <small v-if="logItem.source === 'PRODUCT'">
                      Produktu
                      <template v-if="logItem.product_id">
                        <br><span class="text-primary">{{ getProductName(logItem.product_id) }}</span>
                      </template>
                    </small>
                  </td>
                  <td class="align-middle">
                    <small v-if="logItem.action === 'ADD'" class="text-success">Dodanie</small>
                    <small v-if="logItem.action === 'UPDATE'" class="text-warning">Aktualizacja</small>
                    <small v-if="logItem.action === 'DELETE'" class="text-danger">Usunięcie</small>
                    <small v-if="logItem.action === 'CANCEL'" class="text-dark">Anulowanie</small>
                  </td>
                  <td class="align-middle">
                    <small v-if="logItem.field">{{ getFieldName(logItem.field) }}</small>
                    <small v-else>&mdash;</small>
                  </td>
                  <td class="align-middle">
                    <small v-if="logItem.value" class="text-success">{{ logItem.value }}</small>
                    <small v-else>&mdash;</small>
                  </td>
                  <td class="align-middle">
                    <small v-if="logItem.prev_value" class="text-danger">{{ logItem.prev_value }}</small>
                    <small v-else>&mdash;</small>
                  </td>
                </tr>
              </tbody>
            </table>

            <pagination
              :meta-data="paginationMetaData"
              :initial-page="currentPage" />
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>

    <order-product-popup
      v-if="allDataLoaded && showProductPopup"
      :allDataLoaded="allDataLoaded"
      :products="products"
      :procedures="procedures"
      :materials="materials"
      :polars="polars"
      :seams="seams"
      :sizes="sizes"
      :strands="strands" />

    <order-worker-performance-popup
      v-if="performancePopup.show"
      :performance="performancePopup.performance"
      :workerName="performancePopup.workerName" />

    <remote-job-popup
      :drivers="drivers"
      :workers="workers" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import AppConfig from './../../.env';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import HasPagination from './../../mixins/HasPagination.vue';
import OrderCreator from './../ui/order/OrderCreator.vue';
import OrderEvidence from './../ui/order/OrderEvidence.vue';
import OrderProductPopup from './../popups/OrderProductPopup.vue';
import OrderWorkerPerformancePopup from './../popups/OrderWorkerPerformancePopup.vue';
import Pagination from './../../components/common/Pagination.vue';
import RemoteJob from './../ui/order/RemoteJob.vue';
import RemoteJobPopup from './../ui/order/RemoteJobPopup.vue';
import datepickerPolishTranslation from './../../data/datepicker-translation-pl.js';
import SelectCustomer from './../common/SelectCustomer.vue';
import Vue from 'vue';

export default {
  name: 'order-form',
  mixins: [
    HasPagination
  ],
  components: {
    'order-creator': OrderCreator,
    'order-evidence': OrderEvidence,
    'order-product-popup': OrderProductPopup,
    'order-worker-performance-popup': OrderWorkerPerformancePopup,
    'pagination': Pagination,
    'remote-job': RemoteJob,
    'remote-job-popup': RemoteJobPopup,
    'select-customer': SelectCustomer
  },
  filters: {
    dateFormat (date) {
      if (date && typeof date === 'object') {
        return [date.getDate(), date.getMonth() + 1, date.getFullYear()].map(n => n < 10 ? '0' + n : n).join('-');
      }

      return date;
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded &&
        this.loadedColors &&
        this.loadedDrivers &&
        this.loadedMaterials &&
        this.loadedProcedures &&
        this.loadedProducts &&
        this.loadedPolars &&
        this.loadedSeams &&
        this.loadedSizes &&
        this.loadedStrands &&
        this.loadedWorkers;
    },
    isCancelled () {
      return this.data.status === 'CANCELLED';
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    polishTranslationForDatePicker () {
      return datepickerPolishTranslation;
    },
    otherErrors () {
      let errors = [];

      if (!this.allDataLoaded || !this.isValidated) {
        return errors;
      }

      if (!this.$refs['order-creator'].data.products.length) {
        errors.push('error-2');
      }

      return errors;
    },
    historyErrors () {
      if (this.data.has_unread_logs) {
        return ['error-5'];
      }

      return [];
    },
    validationResults () {
      if (!this.allDataLoaded || !this.$refs['order-form']) {
        return '';
      }

      let errors = this.$refs['order-form'].errors;
      let errorTabs = [];
      let results = [
        ['error-1', ['order_name', 'customer_id', 'planned_shipment_date']]
      ];

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errors[key] && errors[key].length) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.concat(this.otherErrors).join(' ');
    },
    hplUrl () {
      let environment = AppConfig.useConfig;
      return AppConfig.additionalConfig[environment].hplUrl;
    }
  },
  watch: {
    allDataLoaded (newValue) {
      this.$bus.$emit('order-form-all-data-loaded-status', newValue);
    }
  },
  data() {
    return {
      data: {
        id: 0,
        order_name: '',
        customer_id: null,
        sub_customer_name: '',
        planned_shipment_date: null,
        description: '',
        crm_has_order_file: false,
        crm_order_file_url: '',
        created_at: null,
        crm_id: null,
        products: [],
        can_edit: true,
        status: '',
        has_unread_logs: false,
        invoiced: false,
        invoice_number: '',
        products_has_usages: true
      },
      // additional data
      colors: [],
      drivers: [],
      logs: [],
      materials: [],
      procedures: [],
      products: [],
      polars: [],
      seams: [],
      sizes: [],
      strands: [],
      workers: [],
      orderTabTitle: 'Zamówienie',
      // state flags
      currentTab: 'data',
      isValidated: false,
      loaded: false,
      loadedColors: false,
      loadedDrivers: false,
      loadedMaterials: false,
      loadedPolars: false,
      loadedProcedures: false,
      loadedProducts: false,
      loadedSeams: false,
      loadedSizes: false,
      loadedStrands: false,
      loadedWorkers: false,
      loadError: false,
      loadingLogs: false,
      showProductPopup: false,
      markingNotificationsAsReadInProgress: false,
      performancePopup: {
        show: false,
        performance: [],
        workerName: ''
      }
    };
  },
  mounted () {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }

    this.loadAdditionalData();
    this.$bus.$on('order-product-popup-close', this.closeOrderProductPopup);
    this.$bus.$on('order-product-popup-show', this.openOrderProductPopup);
    this.$bus.$on('order-creator-products-updated', this.updateTabTitle);
    this.$bus.$on('order-display-worker-performance-popup', this.showWorkerPerformancePopup);
    this.$bus.$on('order-hide-worker-performance-popup', this.hideWorkerPerformancePopup);
    this.$bus.$on('ui-select-customer-updated', this.updateCustomerID);
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/orders/item/',
        instanceFields: ['id', 'can_edit', 'order_name', 'customer_id', 'customer_name', 'sub_customer_name', 'planned_shipment_date', 'description', 'products', 'crm_has_order_file', 'crm_order_file_url', 'created_at', 'crm_id', 'status', 'has_unread_logs', 'invoiced', 'invoice_number', 'products_has_usages'],
        booleanFields: ['invoiced'],
        loadedKey: 'loaded',
        successAction: () => {
          this.data.planned_shipment_date = this.getDateFromString(this.data.planned_shipment_date);
          this.data.products = this.prepareProductsLoadedFromAPI(this.data.products);
          this.$refs['order-creator'].setUpProducts(this.data.products);
          this.$refs['remote-job'].setUpProducts(this.data.products);
          this.loadFilteredData();
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/colors/items',
        outputKey: 'colors',
        loadedKey: 'loadedColors',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/materials/items',
        outputKey: 'materials',
        loadedKey: 'loadedMaterials',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/polars/items',
        outputKey: 'polars',
        loadedKey: 'loadedPolars',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/procedures/items',
        outputKey: 'procedures',
        loadedKey: 'loadedProcedures',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/products/items',
        outputKey: 'products',
        loadedKey: 'loadedProducts',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/seams/items',
        outputKey: 'seams',
        loadedKey: 'loadedSeams',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/strands/items',
        outputKey: 'strands',
        loadedKey: 'loadedStrands',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            type: 'WORKER'
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'drivers',
        loadedKey: 'loadedDrivers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            type: 'DRIVER'
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/sizes/items',
        outputKey: 'sizes',
        loadedKey: 'loadedSizes',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    validateForm () {
      this.isValidated = true;
      FormUtils.validate(this.$refs['order-form'], this.submitForm);
    },
    submitForm () {
      if (this.invalidShippmentDate) {
        return;
      }

      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit ? '/orders/update/' + this.id : '/orders/store';
      let additionalValidationResults = this.validDataToSend(formDataToSend);

      if (!additionalValidationResults.status) {
        this.$refs['order-creator'].setErrors(additionalValidationResults.errors);

        Vue.swal({
          title: 'Nieprawidłowe dane zamówienia',
          text: 'Niektóre pola procesów są wypełnione nieprawidłowo. Zweryfikuj wartości i spróbuj zapisać zamówienie ponownie.',
          icon: 'error',
          confirmButtonText: 'OK'
        });

        return;
      }

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano zamówienie',
        successText: 'Wybrane zamówienie zostało zapisane',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja zamówienia nie powiodła się. Spróbuj ponownie.',
        successAction: (response) => {
          if (this.isEdit) {
            this.loaded = false;
            this.loadedColors = false;
            this.loadedPolars = false;
            this.loadedProcedures = false;
            this.loadedProducts = false;
            this.loadedSeams = false;
            this.loadedStrands = false;
            this.loadedWorkers = false;
            this.loadedSizes = false;
            this.loadItemData();
            this.loadAdditionalData();
          } else {
            this.$router.push('/zamowienia/edycja/' + parseInt(response.item.id, 10));
          }
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        order_name: this.data.order_name,
        customer_id: this.data.customer_id,
        planned_shipment_date: this.getFormattedDate(this.data.planned_shipment_date),
        products: this.$refs['order-creator'].getProducts(),
        description: this.data.description,
        invoiced: +this.data.invoiced,
        invoice_number: this.data.invoice_number
      };

      return formDataToSend;
    },
    getDateFromString (dateString) {
      if (!dateString) {
        return null;
      }

      dateString = dateString.split('-').map(datePart => parseInt(datePart, 10));
      return new Date(dateString[0], dateString[1] - 1, dateString[2], 0, 0, 0, 0);
    },
    getFormattedDate (date) {
      if (date && typeof date === 'object') {
        return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      }

      return date;
    },
    openOrderProductPopup () {
      this.showProductPopup = true;
    },
    closeOrderProductPopup () {
      this.showProductPopup = false;
    },
    prepareProductsLoadedFromAPI (products) {
      return products.map(product => {
        let optimizedProcesses = product.processes.map(process => ({
          id: process.id,
          process_id: process.process_id,
          name: process.name,
          type: process.type,
          start_at: process.start_at,
          end_at: process.end_at,
          status: process.status,
          items: process.items.map(item => {
            item.is_remote_job = !!item.is_remote_job;
            return item;
          }),
          collection_remote_jobs: process.collection_remote_jobs,
          delivery_remote_jobs: process.delivery_remote_jobs
        }));

        return {
          id: product.id,
          product_id: product.product_id,
          product_name: product.name,
          default_strand_id: product.default_strand_id,
          quantity: product.quantity,
          procedure_id: product.procedure_id,
          size_id: product.size_id,
          size_name: product.size_name,
          color_id: product.color_id,
          color_name: product.color_name,
          polar_id: product.polar_id,
          seam_id: product.seam_id,
          material_name: product.material_name,
          usage_cm_per_item: product.usage_cm_per_item,
          usage_cm: product.usage_cm,
          usage_bolt: product.usage_bolt,
          pattern_required: !!product.pattern_required,
          pattern_stays: !!product.pattern_stays,
          pattern_before_order: !!(product.pattern_required && product.pattern_before_order),
          procedure_start_date: product.procedure_start_date,
          processes: optimizedProcesses,
          deleted: product.deleted,
          has_usage: product.has_usage
        };
      });
    },
    updateTabTitle () {
      if (!this.allDataLoaded || !this.$refs['order-creator'] || !this.$refs['order-creator'].data.products.length) {
        this.orderTabTitle = 'Zamówienie';
      }

      let productsDone = 0;

      for (let i = 0; i < this.$refs['order-creator'].data.products.length; i++) {
        let product = this.$refs['order-creator'].data.products[i];
        let unfinishedProcessFound = false;

        if (!product.procedure_start_date) {
          unfinishedProcessFound = true;
          break;
        }

        if (product.procedure_start_date && process.type !== 'VERIFICATION' && !product.processes.length) {
          unfinishedProcessFound = true;
          break;
        }

        for (let j = 0; j < product.processes.length; j++) {
          let process = product.processes[j];

          if (process.type === 'VERIFICATION' && process.status !== 'DONE') {
            unfinishedProcessFound = true;
            break;
          }

          if (process.type !== 'VERIFICATION') {
            let itemsSum = 0;

            for (let k = 0; k < process.items.length; k++) {
              itemsSum += parseInt(process.items[k].quantity_done, 10);
            }

            if (itemsSum < product.quantity) {
              unfinishedProcessFound = true;
              break;
            }
          }
        }

        if (!unfinishedProcessFound) {
          productsDone++;
        }
      }

      let totalProducts = this.$refs['order-creator'].data.products.filter(product => !product.deleted).length;
      this.orderTabTitle = 'Zamówienie (' + productsDone + ' / ' + totalProducts + ')';
    },
    validDataToSend (dataToSend) {
      let errors = {};
      let status = true;
      let products = JSON.parse(JSON.stringify(dataToSend.products));

      for (let i = 0; i < products.length; i++) {
        let product = products[i];
        errors['order-product-' + i] = {};

        for (let j = 0; j < product.processes.length; j++) {
          let process = product.processes[j];

          for (let k = 0; k < process.items.length; k++) {
            let item = process.items[k];

            if (!item.quantity || Number.isNaN(parseInt(item.quantity, 10)) || parseInt(item.quantity, 10) <= 0) {
              errors['order-product-' + i]['quantity-' + j + '-' + k] = true;
              status = false;
            }

            if (Number.isNaN(parseInt(item.quantity_done, 10)) || parseInt(item.quantity_done, 10) < 0) {
              errors['order-product-' + i]['quantity-done-' + j + '-' + k] = true;
              status = false;
            }

            if (process.type === 'SEWING' && !item.strand_id) {
              errors['order-product-' + i]['strands-' + j + '-' + k] = true;
              status = false;
            }
          }
        }
      }

      return {
        errors,
        status
      };
    },
    showWorkerPerformancePopup (popupData) {
      Vue.set(this.performancePopup, 'performance', JSON.parse(JSON.stringify(popupData.performance)));
      this.performancePopup.workerName = popupData.name;
      this.performancePopup.show = true;
    },
    hideWorkerPerformancePopup () {
      this.performancePopup.show = false;
    },
    getProductName (productID) {
      let foundedProduct = this.data.products.filter(product => product.id === productID);

      if (foundedProduct[0]) {
        let foundedProductFromAPI = this.products.filter(product => product.id === foundedProduct[0].product_id);

        if (foundedProductFromAPI[0]) {
          return foundedProductFromAPI[0].name;
        }
      }

      return '???';
    },
    getFieldName (field) {
      switch (field) {
        case 'crm_has_order_file': return 'Plik zamówienia';
        case 'customer_name': return 'Nazwa klienta';
        case 'order_name': return 'Nazwa zamówienia';
        case 'planned_shipment_date': return 'Planowana data wysyłki';
        case 'description': return 'Opis';
        case 'product_id': return 'ID produktu';
        case 'name': return 'Nazwa produktu';
        case 'code': return 'Kod produktu';
        case 'size_name': return 'Rozmiar';
        case 'polar_name': return 'Polar';
        case 'color_name': return 'Kolor';
        case 'material_name': return 'Materiał';
        case 'pattern_required': return 'Wymaga wykroju';
        case 'pattern_before_order': return 'Wykrajanie przed zamówieniem';
        case 'quantity': return 'Ilość';
        case 'pattern_stays': return 'Wykrój zostaje w hafciarni';
        default: return '???';
      }
    },
    markAllNotificationsAsRead () {
      this.markingNotificationsAsReadInProgress = true;

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/mark-logs-as-read/' + this.data.id,
        errorTitle: 'Wystąpił błąd',
        errorText: 'Oznaczanie powiadomień jako przeczytanych nie powiodło się. Spróbuj ponownie',
        successAction: (data) => {
          if (data.success) {
            this.data.has_unread_logs = false;

            for (let i = 0; i < this.logs.length; i++) {
              this.logs[i].is_read = true;
            }
          }

          this.markingNotificationsAsReadInProgress = false;
        },
        failAction: () => {
          this.markingNotificationsAsReadInProgress = false;
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      this.loadingLogs = true;

      ListUtils.loadItemsData(this, {
        endpoint: '/orders/logs/items?page=' + this.currentPage,
        listField: 'logs',
        pagination: true,
        params: {
          where: {
            order_id: this.data.id
          }
        },
        successAction: () => {
          this.loadingLogs = false;
        }
      });
    },
    updateCustomerID (newValue) {
      this.data.customer_id = newValue;
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-product-popup-close', this.closeOrderProductPopup);
    this.$bus.$off('order-product-popup-show', this.openOrderProductPopup);
    this.$bus.$off('order-creator-products-updated', this.updateTabTitle);
    this.$bus.$off('order-display-worker-performance-popup', this.showWorkerPerformancePopup);
    this.$bus.$off('order-hide-worker-performance-popup', this.hideWorkerPerformancePopup);
    this.$bus.$off('ui-select-customer-updated', this.updateCustomerID);
  }
};
</script>

<style>
.table.is-loading {
  opacity: .25;
}
</style>
